import { FieldQueryOptions, UserId } from '@cibo/core'
import { AcresGtChip, AcresLtChip, FilterChip, FipsChip } from '@cibo/ui'
import { TFunction } from 'i18next'
import { filter, uniq } from 'ramda'
import { FarmChip } from './FarmChip'
import { OwnerChip } from './OwnerChip'
import { splitFilter, yearsString } from './helpers'

type FilterChipProps = {
  filters: FieldQueryOptions
  handleUpdate: (newFilters: FieldQueryOptions) => void
  t: TFunction
}

export const FilterChipComponents = {
  acresLt: ({ filters, handleUpdate }: FilterChipProps) => (
    <AcresLtChip filters={filters} handleUpdate={handleUpdate} />
  ),
  acresGt: ({ filters, handleUpdate }: FilterChipProps) => (
    <AcresGtChip filters={filters} handleUpdate={handleUpdate} />
  ),
  fips: ({ filters, handleUpdate }: FilterChipProps) => (
    <FipsChip filters={filters} handleUpdate={handleUpdate} />
  ),

  lastUpdatedGt: ({
    filters: { lastUpdatedGt, lastUpdatedLt, ...otherFilters },
    handleUpdate,
    t,
  }: FilterChipProps) =>
    !!lastUpdatedGt ? (
      <FilterChip
        onDelete={() => handleUpdate(otherFilters)}
        label={
          !!lastUpdatedLt
            ? t('updatedRange', { lastUpdatedGt, lastUpdatedLt })
            : t('updatedGreaterThan', { lastUpdatedGt })
        }
      />
    ) : null,

  lastUpdatedLt: ({
    filters: { lastUpdatedGt, lastUpdatedLt, ...otherFilters },
    handleUpdate,
    t,
  }: FilterChipProps) =>
    !lastUpdatedGt && lastUpdatedLt ? (
      <FilterChip
        onDelete={() => handleUpdate(otherFilters)}
        label={t('updatedLessThan', { lastUpdatedLt })}
      />
    ) : null,

  fdrStatusAnyOf: ({
    filters: { fdrStatusAnyOf, ...otherFilters },
    handleUpdate,
    t,
  }: FilterChipProps) => (
    <>
      {fdrStatusAnyOf?.map(({ status }) => (
        <FilterChip
          key={status}
          onDelete={() => {
            handleUpdate({
              ...otherFilters,
              fdrStatusAnyOf: fdrStatusAnyOf.filter(
                ({ status: oldStatus }) => oldStatus !== status
              ),
            })
          }}
          label={t(status)}
        />
      ))}
    </>
  ),

  cashCrop: ({ filters: { cashCrop, ...otherFilters }, handleUpdate, t }: FilterChipProps) => (
    <>
      {uniq(cashCrop ? cashCrop?.map(splitFilter(1)) : []).map(cashCropName => (
        <FilterChip
          key={cashCropName}
          onDelete={() => {
            const otherCashCrops = filter(
              (cashCropFilterString: string) =>
                splitFilter(1)(cashCropFilterString) !== cashCropName,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              cashCrop!
            )
            handleUpdate({ ...otherFilters, cashCrop: otherCashCrops })
          }}
          label={t('yearFilter', {
            value: t(cashCropName, { ns: '@cibo/ui/cropNames' }),
            years: yearsString(cashCrop, 5),
          })}
        />
      ))}
    </>
  ),

  coverCrop: ({ filters: { coverCrop, ...otherFilters }, handleUpdate, t }: FilterChipProps) => (
    <>
      {uniq(coverCrop ? coverCrop?.map(splitFilter(1)) : []).map(coverCropName => (
        <FilterChip
          key={coverCropName}
          onDelete={() => {
            const otherCoverCrops = filter(
              (coverCropFilterString: string) =>
                splitFilter(1)(coverCropFilterString) !== coverCropName,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              coverCrop!
            )
            handleUpdate({ ...otherFilters, coverCrop: otherCoverCrops })
          }}
          label={t('yearFilter', {
            value: t(coverCropName, { ns: '@cibo/ui/cropNames' }),
            years: yearsString(coverCrop, 5),
          })}
        />
      ))}
    </>
  ),

  tillage: ({ filters: { tillage, ...otherFilters }, handleUpdate, t }: FilterChipProps) => (
    <>
      {uniq(tillage ? tillage?.map(splitFilter(1)) : [])?.map(tillageName => (
        <FilterChip
          key={tillageName}
          onDelete={() => {
            const otherTillage = filter(
              (tillageFilterString: string) => splitFilter(1)(tillageFilterString) !== tillageName,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              tillage!
            )
            handleUpdate({ ...otherFilters, tillage: otherTillage })
          }}
          label={t('yearFilter', {
            value: t(`tillage_${tillageName}`),
            years: yearsString(tillage, 5),
          })}
        />
      ))}
    </>
  ),

  labels: ({ filters: { labels, ...otherFilters }, handleUpdate, t }: FilterChipProps) => (
    <>
      {labels?.map(val => (
        <FilterChip
          key={val}
          onDelete={() =>
            handleUpdate({
              ...otherFilters,
              labels: filter(label => label !== val, labels),
            })
          }
          label={val}
        />
      ))}
    </>
  ),

  grower: ({ filters: { grower, ...otherFilters }, handleUpdate, t }: FilterChipProps) => {
    const growers = grower?.split(',')

    return (
      <>
        {growers?.map((val: string) => (
          <FilterChip
            key={val}
            onDelete={() => {
              const newGrowers = growers.filter((label: string) => label !== val)
              const newGrowerString = newGrowers.length ? newGrowers.join(',') : undefined
              return handleUpdate({
                ...otherFilters,
                grower: newGrowerString,
              })
            }}
            label={val}
          />
        ))}
      </>
    )
  },

  owner: ({ filters: { owner, ...otherFilters }, handleUpdate, t }: FilterChipProps) =>
    !!owner ? (
      <OwnerChip
        variant="outlined"
        onDelete={() => {
          return handleUpdate({
            ...otherFilters,
            owner: undefined,
          })
        }}
        userId={owner as UserId}
      />
    ) : null,

  termsets: ({ filters: { termsets, ...otherFilters }, handleUpdate, t }: FilterChipProps) => (
    <>
      {termsets
        ?.filter((termsetId: string) => termsetId.indexOf('TSFarm') === 0)
        .map((termsetId: string) => (
          <FarmChip
            key={termsetId}
            onDelete={() => {
              return handleUpdate({
                ...otherFilters,
                termsets: termsets.filter((id: string) => id !== termsetId),
              })
            }}
            termsetId={termsetId}
            variant="outlined"
          />
        ))}
    </>
  ),
}

export type FilterChipName = keyof typeof FilterChipComponents
